import { Product, useUpdateMutation } from '@gripp/shared-logic';
import {
  Colors,
  RoutineButtonData,
  RoutineSubButtons,
  SaveFormTop,
  Spacing,
  TopModalBar,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { array, boolean, number, string, object as yupObject } from 'yup';

type EditProductRoutineModalProps = {
  product: Product;
  isVisible: boolean;
  onClose: () => void;
  productRoutines: RoutineButtonData[];
};

export const EditProductRoutineModal = ({
  product,
  isVisible,
  onClose,
  productRoutines = [],
}: EditProductRoutineModalProps) => {
  const { t } = useTranslation();

  const { mutateAsync: updateProduct } = useUpdateMutation({
    modelName: 'product',
    query: UPDATE_PRODUCT,
  });

  const formSchema = yupObject({
    routines: array().of(
      yupObject({
        routineButtonText: string(),
        title: string(),
        routineId: string(),
        order: number(),
        isProductRoutine: boolean(),
      })
    ),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      routines: productRoutines.filter(routine => routine.isProductRoutine),
    },
    mode: 'onChange',
  });

  useEffect(() => {
    const defaultValues = { routines: productRoutines.filter(routine => routine.isProductRoutine) };
    reset(defaultValues);
  }, [productRoutines, reset]);

  const onCancel = () => {
    onClose();
    reset();
  };

  const onSubmit = async (data: any) => {
    const routineList: RoutineButtonData[] = data.routines;

    // All routines in the form array are selected routines
    const selectedRoutines = routineList.map((routine) => {
      return { id: routine.routineId };
    });
    
    const input = {
      id: product.id,
      routines: selectedRoutines,
    };
    await updateProduct({ input });
    onClose();
  };

  return (
    <Modal
      visible={isVisible}
      animationType="fade"
      transparent={true}
      onRequestClose={onCancel}
    >
      <View style={styles.modalBackground}>
        <View style={styles.modalContainer}>
          <SaveFormTop
            title={t('routines.editRoutines.title')}
            saveLabel={t('topNav.save')}
            onSubmit={handleSubmit(onSubmit)}
            hasBorder={true}
            onCancel={onCancel}
            topBar={TopModalBar}
            isDirty={isDirty}
            isValid={isValid}
          >
            <SafeAreaView style={styles.contentContainer}>
              <ScrollView>
                <Text style={styles.routineDescription}>
                  {t('routines.editRoutines.routineInWorkplace')}
                </Text>
                <RoutineSubButtons
                  routineButtons={productRoutines}
                  control={control}
                  name="routines"
                />
              </ScrollView>
            </SafeAreaView>
          </SaveFormTop>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalBackground: {
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: 560,
    height: 650,
    borderRadius: 12,
    backgroundColor: Colors.primaryGrayBackground,
    overflow: 'hidden',
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
    marginBottom: 0,
    paddingBottom: 60,
    backgroundColor: Colors.primaryGrayBackground,
  },
  routineDescription: {
    paddingBottom: Spacing.basePadding.base / 2,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 16,
  },
});

const UPDATE_PRODUCT = `
  mutation Mutation($input: MutateProductInput!) {
  updateProduct(input: $input) {
    id
  }
}
`;
